/**
 * Frontend mirror of the template tag key derivation rules — how org
 * template schema entity IDs (modules, order forms, schedules) become
 * docxtemplater tag keys in generated Word documents.
 *
 * The backend is canonical (backend/src/utils/templateTagKeys.ts); this
 * mirror powers the schema editor's live tag previews, ID validation, and
 * the docx↔schema tag audit. Both sides are held together by the shared
 * fixture tests/shared/templateTagFixtures/tagKeys.json.
 *
 * Rules (asymmetric by entity — clause IDs, by contrast, are used verbatim,
 * hyphens included):
 * - Module and schedule IDs: hyphens become underscores, case preserved.
 * - Order-form IDs: lowercased, hyphens preserved.
 */

export function moduleIdToKey(moduleId: string): string {
  return moduleId.replace(/-/g, '_');
}

export function orderFormIdToKey(orderFormId: string): string {
  return orderFormId.toLowerCase();
}

export function scheduleIdToKey(scheduleId: string): string {
  return scheduleId.replace(/-/g, '_');
}

/** Conditional tag that wraps a module's section: {#<key>_enabled} … {/<key>_enabled} */
export function moduleEnabledTag(moduleId: string): string {
  return `${moduleIdToKey(moduleId)}_enabled`;
}

/** Conditional tag for an order form within a module's section. */
export function orderFormEnabledTag(moduleId: string, orderFormId: string): string {
  return `${moduleIdToKey(moduleId)}_order_form_${orderFormIdToKey(orderFormId)}_enabled`;
}

/** Conditional tag that wraps a schedule's section. */
export function scheduleEnabledTag(scheduleId: string): string {
  return `${scheduleIdToKey(scheduleId)}_enabled`;
}

/**
 * Write-path ID validation, mirroring the server rules. Module and schedule
 * IDs form the leading part of a docx tag and must start with a letter;
 * order-form IDs are embedded mid-tag, so digit-leading IDs (e.g. '1a') are
 * allowed.
 */
export const MODULE_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
export const SCHEDULE_ID_PATTERN = MODULE_ID_PATTERN;
export const ORDER_FORM_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

export const MODULE_ID_RULE =
  'must start with a letter and contain only letters, numbers, hyphens and underscores (no spaces, max 64 characters)';
export const SCHEDULE_ID_RULE = MODULE_ID_RULE;
export const ORDER_FORM_ID_RULE =
  'must start with a letter or number and contain only letters, numbers, hyphens and underscores (no spaces, max 64 characters)';

/**
 * Message for two IDs deriving the same document tag (e.g. 'a-b' vs 'a_b',
 * or order forms '1A' vs '1a'). Wording mirrors the server — keep in sync.
 */
export function duplicateTagMessage(tag: string, otherLabel: string): string {
  return `derives the same document tag '${tag}' as ${otherLabel}`;
}
